import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Satellite, Battery, Thermometer, Radio, AlertTriangle, CheckCircle, Zap, Globe } from 'lucide-react';

const MissionControlDashboard = () => {
  const [satelliteData, setSatelliteData] = useState({
    status: 'OPERATIONAL',
    altitude: 408.5,
    velocity: 7.66,
    batteryLevel: 87,
    temperature: -15,
    solarPanelOutput: 2.3,
    signalStrength: 92,
    latitude: 25.2048,
    longitude: 55.2708,
    lastContact: new Date().toLocaleTimeString('en-US')
  });

  const [telemetryHistory, setTelemetryHistory] = useState([]);
  const [batteryHistory, setBatteryHistory] = useState([]);
  const [temperatureHistory, setTemperatureHistory] = useState([]);

  // Real-time data simulation
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-US', { hour12: false });
      
      setSatelliteData(prev => ({
        ...prev,
        altitude: 408.5 + Math.sin(Date.now() / 10000) * 2,
        velocity: 7.66 + Math.sin(Date.now() / 8000) * 0.1,
        batteryLevel: Math.max(20, Math.min(100, prev.batteryLevel + (Math.random() - 0.5) * 2)),
        temperature: -15 + Math.sin(Date.now() / 12000) * 8,
        solarPanelOutput: Math.max(0, 2.3 + Math.sin(Date.now() / 15000) * 1.2),
        signalStrength: 90 + Math.sin(Date.now() / 7000) * 8,
        latitude: 25.2048 + Math.sin(Date.now() / 20000) * 10,
        longitude: 55.2708 + Math.cos(Date.now() / 18000) * 15,
        lastContact: timeStr
      }));

      // Update telemetry history
      setTelemetryHistory(prev => {
        const newData = [...prev, {
          time: timeStr,
          altitude: 408.5 + Math.sin(Date.now() / 10000) * 2,
          velocity: 7.66 + Math.sin(Date.now() / 8000) * 0.1,
          signalStrength: 90 + Math.sin(Date.now() / 7000) * 8
        }].slice(-20);
        return newData;
      });

      setBatteryHistory(prev => {
        const newData = [...prev, {
          time: timeStr,
          battery: Math.max(20, Math.min(100, prev[prev.length - 1]?.battery || 87 + (Math.random() - 0.5) * 2)),
          solarOutput: Math.max(0, 2.3 + Math.sin(Date.now() / 15000) * 1.2)
        }].slice(-20);
        return newData;
      });

      setTemperatureHistory(prev => {
        const newData = [...prev, {
          time: timeStr,
          temperature: -15 + Math.sin(Date.now() / 12000) * 8
        }].slice(-20);
        return newData;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPERATIONAL': return 'text-green-400';
      case 'WARNING': return 'text-yellow-400';
      case 'CRITICAL': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPERATIONAL': return <CheckCircle className="w-5 h-5" />;
      case 'WARNING': return <AlertTriangle className="w-5 h-5" />;
      case 'CRITICAL': return <AlertTriangle className="w-5 h-5" />;
      default: return <Radio className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <Satellite className="w-8 h-8 text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Mission Control Dashboard</h1>
              <p className="text-gray-400">Satellite Monitoring System</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400">Last Contact</div>
            <div className="text-lg font-mono">{satelliteData.lastContact}</div>
          </div>
        </div>
        
        {/* Satellite Status */}
        <div className="flex items-center space-x-3 bg-gray-800 rounded-lg p-4">
          <div className={`flex items-center space-x-2 ${getStatusColor(satelliteData.status)}`}>
            {getStatusIcon(satelliteData.status)}
            <span className="font-semibold">{satelliteData.status}</span>
          </div>
          <div className="text-gray-400">|</div>
          <div className="text-gray-300">System Status: Active</div>
        </div>
      </div>

      {/* Main Data Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Globe className="w-6 h-6 text-blue-400" />
            <span className="text-sm text-gray-400">Altitude</span>
          </div>
          <div className="text-2xl font-bold">{satelliteData.altitude.toFixed(1)} km</div>
          <div className="text-sm text-gray-400">Above Earth</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-6 h-6 text-yellow-400" />
            <span className="text-sm text-gray-400">Velocity</span>
          </div>
          <div className="text-2xl font-bold">{satelliteData.velocity.toFixed(2)} km/s</div>
          <div className="text-sm text-gray-400">Orbital</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Battery className="w-6 h-6 text-green-400" />
            <span className="text-sm text-gray-400">Battery</span>
          </div>
          <div className="text-2xl font-bold">{satelliteData.batteryLevel.toFixed(0)}%</div>
          <div className={`text-sm ${satelliteData.batteryLevel > 50 ? 'text-green-400' : satelliteData.batteryLevel > 20 ? 'text-yellow-400' : 'text-red-400'}`}>
            {satelliteData.batteryLevel > 50 ? 'Good' : satelliteData.batteryLevel > 20 ? 'Fair' : 'Low'}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Thermometer className="w-6 h-6 text-red-400" />
            <span className="text-sm text-gray-400">Temperature</span>
          </div>
          <div className="text-2xl font-bold">{satelliteData.temperature.toFixed(1)}°C</div>
          <div className="text-sm text-gray-400">Internal</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Orbital Data Chart */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Orbital Data</h3>
          {/* <ResponsiveContainer width="100%" height={300}>
            <LineChart data={telemetryHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} domain={[7, 8]} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="altitude" stroke="#60A5FA" strokeWidth={2} name="Altitude (km)" />
              <Line type="monotone" dataKey="velocity" stroke="#34D399" strokeWidth={2} name="Velocity (km/s)" />
            </LineChart>
          </ResponsiveContainer> */}

          <ResponsiveContainer width="100%" height={300}>
  <LineChart data={telemetryHistory}>
    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
    <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
    <YAxis yAxisId="altitude" orientation="left" stroke="#60A5FA" domain={[300, 500]} />
    <YAxis yAxisId="velocity" orientation="right" stroke="#34D399" domain={[7, 8]} />
    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
    <Line yAxisId="altitude" type="monotone" dataKey="altitude" stroke="#60A5FA" strokeWidth={2} name="Altitude (km)" />
    <Line yAxisId="velocity" type="monotone" dataKey="velocity" stroke="#34D399" strokeWidth={2} name="Velocity (km/s)" />
  </LineChart>
</ResponsiveContainer>
        </div>

        {/* Power Systems Chart */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Power Systems</h3>
          <ResponsiveContainer width="100%" height={300}>
            {/* <AreaChart data={batteryHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
              />
              <Area type="monotone" dataKey="battery" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.3} name="Battery %" />
              <Area type="monotone" dataKey="solarOutput" stackId="2" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} name="Solar Output (kW)" />
            </AreaChart> */}
            <AreaChart data={batteryHistory}>
  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
  <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
  <YAxis yAxisId="battery" orientation="left" stroke="#10B981" domain={[0, 100]} />  {/* للبطارية */}
  <YAxis yAxisId="solar" orientation="right" stroke="#F59E0B" domain={[0, 5]} />     {/* للـ solar */}

  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />

  <Area yAxisId="battery" type="monotone" dataKey="battery" stroke="#10B981" fill="#10B981" fillOpacity={0.4} name="Battery %" />
  <Area yAxisId="solar" type="monotone" dataKey="solarOutput" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.4} name="Solar Output (kW)" />
</AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Temperature and Location */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Temperature Monitor</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={temperatureHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
              />
              <Area type="monotone" dataKey="temperature" stroke="#EF4444" fill="#EF4444" fillOpacity={0.2} name="Temperature (°C)" baseValue="dataMin" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Location Information */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Location & Communications</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Latitude:</span>
              <span className="font-mono text-blue-400">{satelliteData.latitude.toFixed(4)}°</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Longitude:</span>
              <span className="font-mono text-green-400">{satelliteData.longitude.toFixed(4)}°</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Signal Strength:</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${satelliteData.signalStrength}%` }}
                  ></div>
                </div>
                <span className="text-sm">{satelliteData.signalStrength.toFixed(0)}%</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Solar Output:</span>
              <span className="font-mono text-yellow-400">{satelliteData.solarPanelOutput.toFixed(1)} kW</span>
            </div>
          </div>
        </div>
      </div>

      {/* Control Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Status */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">System Status</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span>Communication</span>
              </div>
              <span className="text-green-400">Active</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span>Navigation</span>
              </div>
              <span className="text-green-400">Active</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Solar Panels</span>
              </div>
              <span className="text-yellow-400">Limited</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span>Thermal Control</span>
              </div>
              <span className="text-green-400">Active</span>
            </div>
          </div>
        </div>

        {/* Control Commands */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Control Commands</h3>
          <div className="space-y-3">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors">
              Ping Command
            </button>
            <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors">
              Orbital Adjustment
            </button>
            <button className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg transition-colors">
              Emergency Mode
            </button>
            <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors">
              Data Analysis
            </button>
          </div>
        </div>

        {/* Mission Updates */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Mission Updates</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <div className="text-green-400 font-medium">Communication Established</div>
                <div className="text-gray-400">Signal acquired successfully</div>
                <div className="text-xs text-gray-500">2 minutes ago</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <div className="text-yellow-400 font-medium">Solar Panel Efficiency</div>
                <div className="text-gray-400">Output reduced due to shadow</div>
                <div className="text-xs text-gray-500">5 minutes ago</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <div className="text-blue-400 font-medium">Orbital Parameters</div>
                <div className="text-gray-400">Trajectory updated successfully</div>
                <div className="text-xs text-gray-500">12 minutes ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Information */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Mission Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold text-blue-400 mb-2">Launch Information</h4>
            <div className="space-y-1 text-gray-300">
              <div>Launch Date: March 15, 2024</div>
              <div>Launch Vehicle: Falcon 9</div>
              <div>Mass: 2,300 kg</div>
              <div>Mission Duration: 5 years</div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-green-400 mb-2">Mission Objectives</h4>
            <div className="space-y-1 text-gray-300">
              <div>• Weather Monitoring</div>
              <div>• Communications Relay</div>
              <div>• Earth Observation</div>
              <div>• Scientific Research</div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-purple-400 mb-2">Mission Team</h4>
            <div className="space-y-1 text-gray-300">
              <div>Mission Commander: Dr. Sarah Chen</div>
              <div>Systems Engineer: Mark Rodriguez</div>
              <div>Flight Director: Emily Watson</div>
              <div>Data Analyst: James Kim</div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="mt-8 bg-gray-800 rounded-lg p-4">
        <div className="flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Simulation Active</span>
            </div>
          </div>
          <div className="text-gray-400">
            Auto-refresh every 2 seconds
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionControlDashboard;